/**
 * Cart-drawer controller. Public API on `window.CartDrawer` (the instance):
 *
 *   Mutations
 *     update(mutations, { source })  -> Promise<cart>   single choke point:
 *         POSTs each mutation, re-renders the drawer, emits lifecycle events,
 *         and owns the loading state. Every drawer-originated cart change
 *         should flow through here.
 *     switchProduct(props)           -> Promise<cart>   subscription up/downgrade
 *     refreshDrawer()                -> Promise         re-render only (no mutation)
 *
 *   State (read-only)
 *     state.loading  bool     a mutation is in flight
 *     state.source   string   what triggered it (e.g. 'sub-upgrade')
 *     state.cart     object   last cart JSON seen
 *
 *   Events (dispatched on document, bubbling)
 *     cart:updating  detail { source, mutations }
 *     cart:updated   detail { source, cart }
 *     cart:error     detail { source, error }
 *   The legacy bare `refreshCart` event still fires from refreshDrawer() for
 *   back-compat with existing listeners.
 *
 *   NOTE: setLoading() toggles an `is-loading` class on the drawer offcanvas.
 *   cart-drawer.css uses that state to show the drawer-scoped loading overlay.
 *
 * Recharge catalog logic lives in assets/recharge.js (window.Recharge), which
 * must load first (both are deferred, in document order).
 *
 * Loads on every layout that renders sections/cart-drawer.liquid:
 * layout/theme.liquid + layout/theme.faq.liquid.
 */
(function () {
    'use strict';

    const CHECKOUT_BUTTON_LOADING_MS = 5000;
    const DRAWER_LOADING_MIN_MS = 350;
    const DRAWER_LOADING_TIMEOUT_MS = 4000;

    const SELECTORS = {
        drawerContainer: '#offcanvas-cart-drawer',
        externalMutationTrigger:
            '#cartDrawerContainer .js-cart-drawer-qty, #cartDrawerContainer .cart-drawer-cross-sell__atc-button',
        siteDiscountBarInDrawer: "[data-site-discount-bar-type='cart-drawer']",
        siteDiscountBarInHeader: "[data-site-discount-bar-type='site-header']",
        subLabel: '.cart-drawer-sub-toggle__label',
    };

    const CART_ACTIONS = {
        CHECKOUT: 'cart-drawer-checkout',
        SUB_UPGRADE: 'sub-upgrade',
        SUB_DOWNGRADE: 'sub-downgrade',
    };

    class CartDrawer {
        constructor() {
            this.state = { loading: false, source: null, cart: null };
            this.externalLoadingSource = null;
            this.loadingHideTimer = null;
            this.loadingShownAt = 0;
            this.loadingTimeoutTimer = null;
        }

        init() {
            if (this._inited) return;
            this._inited = true;
            document.body.addEventListener('click', this.handleClick.bind(this));
            document.addEventListener('click', this.handleExternalMutationClick.bind(this), true);
            document.addEventListener('refreshCart', this.hideExternalLoading.bind(this));
            document.addEventListener('cart:updated', this.hideExternalLoading.bind(this));
            document.addEventListener('cart:error', this.hideExternalLoading.bind(this));
        }

        // ---- events + loading (the single hook points for external code) ----

        emit(name, detail) {
            document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
        }

        setLoading(loading, source = null) {
            clearTimeout(this.loadingHideTimer);
            const drawer = document.querySelector(SELECTORS.drawerContainer);

            if (loading) {
                clearTimeout(this.loadingTimeoutTimer);
                this.state.loading = true;
                this.state.source = source;
                this.loadingShownAt = Date.now();
                if (drawer) drawer.classList.add('is-loading');
                return;
            }

            const hide = () => {
                this.state.loading = false;
                this.state.source = null;
                const currentDrawer = document.querySelector(SELECTORS.drawerContainer);
                if (currentDrawer) currentDrawer.classList.remove('is-loading');
            };
            const elapsed = Date.now() - this.loadingShownAt;
            const remaining = DRAWER_LOADING_MIN_MS - elapsed;

            clearTimeout(this.loadingTimeoutTimer);
            if (remaining > 0) {
                this.loadingHideTimer = setTimeout(hide, remaining);
            } else {
                hide();
            }
        }

        handleExternalMutationClick(event) {
            if (!event.target?.closest) return;
            const trigger = event.target.closest(SELECTORS.externalMutationTrigger);
            if (!trigger) return;
            if (
                trigger.dataset.cartAction === CART_ACTIONS.SUB_UPGRADE ||
                trigger.dataset.cartAction === CART_ACTIONS.SUB_DOWNGRADE
            ) {
                return;
            }

            const source = trigger.classList.contains('js-cart-drawer-qty') ? 'qty-change' : 'drawer-cross-sell-add';
            this.showExternalLoading(source);
        }

        showExternalLoading(source) {
            this.externalLoadingSource = source;
            this.setLoading(true, source);
            clearTimeout(this.loadingTimeoutTimer);
            this.loadingTimeoutTimer = setTimeout(() => this.hideExternalLoading(), DRAWER_LOADING_TIMEOUT_MS);
        }

        hideExternalLoading() {
            if (!this.externalLoadingSource) return;
            this.externalLoadingSource = null;
            clearTimeout(this.loadingTimeoutTimer);
            this.setLoading(false);
        }

        // ---- the mutation choke point ----

        // mutations: [{ path: 'cart/change.js', body: {...} }, ...] applied in
        // order, then one drawer re-render. Owns loading + lifecycle events.
        async update(mutations, { source = 'unknown' } = {}) {
            this.setLoading(true, source);
            this.emit('cart:updating', { source, mutations });
            try {
                for (const m of mutations) {
                    await this.postCartJson(m.path, m.body);
                }
                await this.refreshDrawer();
                this.state.cart = await this.fetchCart();
                this.emit('cart:updated', { source, cart: this.state.cart });
                return this.state.cart;
            } catch (error) {
                console.error(`[cart-drawer] update (${source}) failed`, error);
                this.emit('cart:error', { source, error });
                throw error;
            } finally {
                this.setLoading(false, source);
            }
        }

        // ---- click delegation (survives every re-render) ----

        handleClick(event) {
            if (!event.target?.closest) return;
            const target = event.target.closest('[data-cart-action]');
            if (!target) return;

            switch (target.dataset.cartAction) {
                case CART_ACTIONS.CHECKOUT:
                    this.showCheckoutLoadingState(target);
                    break;
                case CART_ACTIONS.SUB_UPGRADE:
                    this.showSubLoadingState(target, 'Upgrading');
                    this.switchProduct(this.readSubToggleProps(target));
                    break;
                case CART_ACTIONS.SUB_DOWNGRADE:
                    this.showSubLoadingState(target, 'Downgrading');
                    this.switchProduct(this.readSubToggleProps(target));
                    break;
            }
        }

        readSubToggleProps(toggleEl) {
            const d = toggleEl.dataset;
            return {
                variantId: Number(d.variantId),
                quantity: Number(d.quantity),
                itemKey: d.itemKey,
                itemTotal: d.itemTotal,
                sellingPlanId: Number(d.sellingPlanId) || 0,
            };
        }

        showCheckoutLoadingState(buttonEl) {
            buttonEl.classList.add('button--loading');
            setTimeout(() => {
                buttonEl.classList.remove('button--loading');
            }, CHECKOUT_BUTTON_LOADING_MS);
        }

        // Visual state on the sub toggle while its mutation is in flight. All
        // styling lives in the `--loading` CSS modifier (cart-drawer.css); the
        // animated ellipsis stops on its own when the re-render drops the node.
        showSubLoadingState(toggleEl, label) {
            toggleEl.classList.add('cart-drawer-sub-toggle--loading');
            const labelEl = toggleEl.querySelector(SELECTORS.subLabel);
            if (labelEl) {
                labelEl.textContent = label;
                labelEl.classList.add('is-loading-ellipsis');
            }
        }

        // ---- subscription up/downgrade ----

        async switchProduct({ variantId, quantity, itemKey, itemTotal, sellingPlanId }) {
            const cdnProducts = await window.Recharge.getProducts();
            const cart = await this.fetchCart();

            if (!cart?.items?.length) {
                console.warn('[cart-drawer] switchProduct: cart is empty');
                return;
            }

            const mapped = window.Recharge.mapCartItems(cart.items, cdnProducts);
            const cartItem = mapped.find((m) => m.key === itemKey);
            const variantToAdd = cartItem?.variants?.find((v) => Number(v.id) === variantId);

            const isToggleOn = sellingPlanId === 0;
            const addThis = {
                id: variantId,
                quantity,
                properties: { item_total: itemTotal },
            };
            if (isToggleOn && variantToAdd) {
                addThis.selling_plan = variantToAdd.selling_plan_allocations[0]?.selling_plan_id;
            }

            return this.update(
                [
                    { path: 'cart/update.js', body: { updates: { [itemKey]: 0 } } },
                    { path: 'cart/add.js', body: addThis },
                ],
                { source: isToggleOn ? 'sub-upgrade' : 'sub-downgrade' }
            );
        }

        // ---- fetch helpers ----

        async postCartJson(path, body) {
            const url = `${window.Shopify.routes.root}${path}`;
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body),
            });
            if (!response.ok) {
                throw new Error(`[cart-drawer] POST ${url} returned ${response.status}`);
            }
            return response.json();
        }

        async fetchCart() {
            try {
                const response = await fetch(`${window.Shopify.routes.root}cart.js`);
                return await response.json();
            } catch (e) {
                console.error('[cart-drawer] fetchCart failed', e);
                return {};
            }
        }

        // ---- re-render (no mutation) ----

        // Re-fetches the cart-drawer section, swaps it into the offcanvas
        // container, then re-fetches and re-hydrates the site discount bar in
        // the page header. Both fetches run in parallel. Fires the legacy
        // `refreshCart` event for back-compat.
        async refreshDrawer() {
            await Promise.all([this.refreshDrawerSection(), this.refreshHeaderDiscountBar()]);
            document.dispatchEvent(new Event('refreshCart', { bubbles: true }));
        }

        async refreshDrawerSection() {
            try {
                const response = await fetch('/?section_id=cart-drawer');
                const html = await response.text();
                const drawerEl = document.querySelector(SELECTORS.drawerContainer);
                if (!drawerEl) return;
                drawerEl.innerHTML = html;

                const innerBar = drawerEl.querySelector(SELECTORS.siteDiscountBarInDrawer);
                if (innerBar) {
                    window.CartProgress?.destroyWithin?.(innerBar);
                    window.CartProgress?.hydrate?.(innerBar);
                }

                window.cartUpsell?.();
            } catch (e) {
                console.error('[cart-drawer] refreshDrawerSection failed', e);
            }
        }

        async refreshHeaderDiscountBar() {
            try {
                const response = await fetch('/?section_id=site-discount-bar-site-header');
                const html = await response.text();
                const headerBar = document.querySelector(SELECTORS.siteDiscountBarInHeader);
                if (!headerBar) return;
                window.CartProgress?.destroyWithin?.(headerBar);
                headerBar.innerHTML = html;
                window.CartProgress?.hydrate?.(headerBar);
            } catch (e) {
                console.error('[cart-drawer] refreshHeaderDiscountBar failed', e);
            }
        }
    }

    const instance = new CartDrawer();
    // Public API: the instance. `window.cartDrawer` stays a callable bootstrap
    // for back-compat with any existing caller.
    window.CartDrawer = instance;
    window.cartDrawer = function () {
        instance.init();
    };

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => instance.init());
    } else {
        instance.init();
    }
})();
